/**
 * Bottom sheets: short, focused tasks that slide up over the camera.
 * Each is a labelled dialog; Escape or tapping outside closes it.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Check, X } from "lucide-react";
import type { CalendarDate, Verdict } from "@hitl/rotations-domain";
import type { SpotView } from "../state/session";
import { groupByClient, LIGHT_ICON } from "./parts";
import { VERDICT_WORD } from "./copy";

export function Sheet(props: { title: string; onClose: () => void; children: ReactNode; focusField?: boolean }) {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(props.onClose);
  onCloseRef.current = props.onClose;
  const focusField = props.focusField ?? false;
  // Once per opening: focus and listen for Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onCloseRef.current();
    window.addEventListener("keydown", onKey);
    // Sheets whose job IS typing take the field (the opener already raised the iOS keyboard via
    // the keyboard proxy, so focus moves across and the keyboard stays up). Sheets that lead
    // with choices focus the dialog itself: no keyboard covering the choices, no stray ring.
    const field = focusField ? ref.current?.querySelector<HTMLElement>("input, textarea") : null;
    (field ?? ref.current)?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <div className="sheet-backdrop" onClick={props.onClose}>
      <div className="sheet" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} ref={ref} onClick={(e) => e.stopPropagation()}>
        <div className="sheet__head">
          <h2 id={titleId}>{props.title}</h2>
          <button className="sheet__close" onClick={props.onClose} aria-label="Close">
            <X aria-hidden size={22} />
          </button>
        </div>
        {props.children}
      </div>
    </div>
  );
}

export function TypeNumberSheet(props: { onSubmit: (value: string) => void; onClose: () => void }) {
  const [value, setValue] = useState("");
  const valid = /^[A-Za-z]{1,2}\d{1,3}$/.test(value.trim());
  return (
    <Sheet title="Type the pot number" onClose={props.onClose} focusField>
      <form
        className="sheet__body"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) props.onSubmit(value);
        }}
      >
        <label className="field">
          <span className="field__label">Number on the sticker</span>
          <input
            className="field__input field__input--big"
            value={value}
            onChange={(e) => setValue(e.target.value.toUpperCase())}
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            placeholder="A1"
            inputMode="text"
            maxLength={5}
          />
        </label>
        {value && !valid && <p className="field__hint">Letters then numbers, like A1 or B12.</p>}
        <button className="btn btn--light btn--block" type="submit" disabled={!valid}>
          Check this pot
        </button>
      </form>
    </Sheet>
  );
}

const REFUSAL_REASONS = ["Colour", "Size", "Style", "Condition"];

export function RefuseSheet(props: { potId: string; clientName: string; onSubmit: (reason: string) => void; onClose: () => void }) {
  const [other, setOther] = useState("");
  return (
    <Sheet title={`${props.clientName} doesn't want ${props.potId}`} onClose={props.onClose}>
      <div className="sheet__body">
        <p className="sheet__lead">Why not? One tap is enough.</p>
        <div className="choices">
          {REFUSAL_REASONS.map((reason) => (
            <button key={reason} className="choice" onClick={() => props.onSubmit(reason)}>
              {reason}
            </button>
          ))}
        </div>
        <label className="field">
          <span className="field__label">Something else</span>
          <input className="field__input" value={other} onChange={(e) => setOther(e.target.value)} placeholder="e.g. too tall for the counter" />
        </label>
        <button className="btn btn--light btn--block" disabled={!other.trim()} onClick={() => props.onSubmit(other.trim())}>
          Save reason
        </button>
      </div>
    </Sheet>
  );
}

/** Why a red is being overridden decides what we explain and which reasons we offer. */
export type OverrideKind = "repeat" | "retired" | "unknown";

const OVERRIDE_REASONS: Record<OverrideKind, string[]> = {
  repeat: ["Client asked for it", "No other pot fits"],
  retired: ["Back in use", "No other pot fits"],
  // Things outside her control: the platform misread the code, or the record is behind reality.
  unknown: ["Sticker misread", "Sticker damaged", "New pot, not listed yet"],
};

function overrideLead(kind: OverrideKind, potId: string, clientName: string): string {
  switch (kind) {
    case "repeat":
      return `This breaks the 12-month promise to ${clientName}. The reason is kept with the record.`;
    case "retired":
      return `${potId} is marked as retired. The reason is kept with the record.`;
    case "unknown":
      return `${potId} isn't in your pot list. If the sticker was read wrong, typing the right number is better. Otherwise it's recorded with your reason so the list can be fixed later.`;
  }
}

export function OverrideSheet(props: {
  potId: string;
  clientName: string;
  kind: OverrideKind;
  onSubmit: (reason: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  return (
    <Sheet title={`Place ${props.potId} anyway?`} onClose={props.onClose}>
      <div className="sheet__body">
        <p className="sheet__lead">{overrideLead(props.kind, props.potId, props.clientName)}</p>
        <div className="choices">
          {OVERRIDE_REASONS[props.kind].map((r) => (
            <button key={r} className={`choice${reason === r ? " is-picked" : ""}`} onClick={() => setReason(r)} aria-pressed={reason === r}>
              {r}
            </button>
          ))}
        </div>
        <label className="field">
          <span className="field__label">Reason</span>
          <input className="field__input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why it's going here" />
        </label>
        <button className="btn btn--danger btn--block" disabled={!reason.trim()} onClick={() => props.onSubmit(reason.trim())}>
          Place {props.potId} anyway
        </button>
      </div>
    </Sheet>
  );
}

export function RouteSheet(props: {
  spots: readonly SpotView[];
  currentIndex: number;
  today: CalendarDate;
  onPick: (index: number) => void;
  onScanAny: () => void;
  onResetDemo: () => void;
  onOpenPlan?: () => void;
  onClose: () => void;
}) {
  const groups = groupByClient(props.spots);
  return (
    <Sheet title="Today's route" onClose={props.onClose}>
      <div className="sheet__body">
        <ol className="route">
          {groups.map((g, i) => (
            <li key={`${g.clientId}-${i}`} className="route__stop">
              <p className="route__client">
                <span className="route__num">{i + 1}</span>
                {g.spots[0]?.clientName}
              </p>
              <ul>
                {g.spots.map((s) => (
                  <li key={s.slotId}>
                    <button className={`route__spot${s.index === props.currentIndex ? " is-current" : ""}`} onClick={() => props.onPick(s.index)}>
                      <span>{s.spotLabel}</span>
                      <span className={s.done ? "route__state is-done" : "route__state"}>
                        {s.done && <Check aria-hidden size={16} strokeWidth={2.75} />}
                        {s.done ? `Done · ${s.done.potId}` : s.plannedPotId ? `Planned ${s.plannedPotId}` : "Open"}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
        <button className="btn btn--light btn--block" onClick={props.onScanAny}>
          Scan any pot: where can it go?
        </button>
        {props.onOpenPlan && (
          <button className="btn btn--ghost btn--block" onClick={props.onOpenPlan}>
            Plan the week
          </button>
        )}
        <button className="btn btn--quiet btn--block" onClick={props.onResetDemo}>
          Reset demo data
        </button>
      </div>
    </Sheet>
  );
}

export function WhereCanItGoSheet(props: {
  potId: string;
  standing: string;
  options: { spot: SpotView; verdict: Verdict }[];
  onPick: (index: number) => void;
  onClose: () => void;
}) {
  return (
    <Sheet title={`Where can ${props.potId} go?`} onClose={props.onClose}>
      <div className="sheet__body">
        <p className="sheet__lead">{props.standing}</p>
        <ul className="where">
          {props.options.map(({ spot, verdict }) => (
            <li key={spot.slotId}>
              <button className="where__row" onClick={() => props.onPick(spot.index)}>
                <span>
                  {spot.clientName}
                  <small>{spot.spotLabel}</small>
                </span>
                <span className={`mini mini--${verdict.light}`}>
                  {(() => {
                    const Icon = LIGHT_ICON[verdict.light];
                    return <Icon aria-hidden size={14} strokeWidth={2.75} />;
                  })()}
                  {VERDICT_WORD[verdict.light]}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Sheet>
  );
}

/** "More" for the scanned pot: the less frequent actions, kept off the verdict. */
export function PotActionsSheet(props: {
  potId: string;
  canWriteOff: boolean;
  onType: () => void;
  onWriteOff: () => void;
  onClose: () => void;
}) {
  return (
    <Sheet title={`More for ${props.potId}`} onClose={props.onClose}>
      <div className="sheet__body">
        <button className="btn btn--ghost btn--block" onClick={props.onType}>
          Type a different number
        </button>
        {props.canWriteOff && (
          <button className="btn btn--quiet btn--block" onClick={props.onWriteOff}>
            Write off <span className="inline-pot">{props.potId}</span>…
          </button>
        )}
      </div>
    </Sheet>
  );
}
